import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

const LIME = "#ccff00";
const anton = { fontFamily: "Anton, Archivo, sans-serif" };
const UPDATED = "June 2026";

export const LEGAL_LINKS = [
  { slug: "terms", label: "Terms of Service" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "cookies", label: "Cookie Policy" },
  { slug: "refunds", label: "Returns & Refunds" },
  { slug: "disclaimer", label: "Disclaimer" },
  { slug: "imprint", label: "Contact & Imprint" },
];

export const LEGAL_DEFAULTS = {
  terms: {
    title: "Terms of Service",
    intro: "These Terms govern your access to and use of StoreIG, a platform operated by CasperBrands. By creating an account, opening a storefront, or making a purchase, you agree to these Terms.",
    sections: [
      { h: "1. The platform", p: "StoreIG lets merchants build and run Instagram-style online storefronts. We provide the hosting, checkout and dashboard tools; each merchant is solely responsible for the products they list, their descriptions, pricing, fulfilment and customer service." },
      { h: "2. Accounts", p: "You must provide accurate information and keep your login credentials secure. You are responsible for all activity that happens under your account. We may suspend or terminate accounts that breach these Terms or applicable law." },
      { h: "3. Merchant responsibilities", p: "Merchants warrant that they have the right to sell the products they list, that listings are lawful and not misleading, and that they will honour orders placed through their storefront. Prohibited goods (illegal, counterfeit, or restricted items) are not permitted." },
      { h: "4. Payments", p: "Payments are processed by Stripe. StoreIG does not store full card details. Merchants are responsible for their own tax obligations, VAT and any chargebacks arising from their sales." },
      { h: "5. Availability", p: "We work hard to keep StoreIG online but do not guarantee uninterrupted service. Features may change, and scheduled or emergency maintenance may occur." },
      { h: "6. Liability", p: "To the maximum extent permitted by law, StoreIG and CasperBrands are not liable for indirect or consequential losses arising from your use of the platform. Nothing in these Terms limits liability that cannot be excluded by law." },
      { h: "7. Changes", p: "We may update these Terms from time to time. Material changes will be posted on this page with a new effective date. Continued use of the platform means you accept the updated Terms." },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro: "This Policy explains how CasperBrands collects, uses and protects personal data when you use StoreIG.",
    sections: [
      { h: "1. Data we collect", p: "Account data (name, email), storefront content you upload, order and payment metadata (processed via Stripe), newsletter sign-ups, and technical data such as IP address and device information for security and analytics." },
      { h: "2. How we use it", p: "To operate your storefront, process orders, prevent fraud and abuse, provide support, send transactional and (where opted-in) marketing emails, and improve the platform." },
      { h: "3. Sharing", p: "We share data with processors that make the service work — notably Stripe (payments), our email provider, and shipping partners you enable. We never sell your personal data." },
      { h: "4. Retention", p: "We keep personal data only as long as needed to provide the service and to meet legal, tax and accounting obligations." },
      { h: "5. Your rights", p: "Depending on your location, you may have the right to access, correct, export or delete your personal data, and to object to certain processing. Contact us to exercise these rights." },
      { h: "6. Contact", p: "For any privacy request, email privacy@storeig.com." },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    intro: "StoreIG uses cookies and similar technologies to keep the platform working and to understand how it is used.",
    sections: [
      { h: "1. Essential cookies", p: "Required for login sessions, security and the shopping cart. The platform will not work correctly without them." },
      { h: "2. Analytics", p: "Help us understand aggregate usage — which pages are visited and how features perform — so we can improve StoreIG. These are only set where permitted." },
      { h: "3. Managing cookies", p: "You can control cookies through your browser settings and via any cookie banner shown on a storefront. Disabling essential cookies may break checkout and login." },
    ],
  },
  refunds: {
    title: "Returns & Refunds",
    intro: "Each storefront on StoreIG is run by an independent merchant. Returns and refunds are handled by the merchant you purchased from, subject to the policy shown at their store and to your statutory rights.",
    sections: [
      { h: "1. Requesting a return", p: "Contact the merchant directly using the email on your order confirmation. Include your order reference and the reason for the return." },
      { h: "2. Statutory rights", p: "Consumers in the EU/EEA generally have a 14-day right of withdrawal for most online purchases. This right is separate from, and in addition to, any merchant policy." },
      { h: "3. Refund processing", p: "Approved refunds are issued to the original payment method via Stripe. Processing times depend on your bank or card provider." },
      { h: "4. Disputes", p: "If you cannot resolve an issue with a merchant, you may open a dispute with your payment provider. StoreIG can assist with order records where appropriate." },
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    intro: "StoreIG is an independent platform. Please read the following carefully.",
    sections: [
      { h: "Not affiliated with Instagram or Meta", p: "StoreIG is an independent product and is not affiliated with, endorsed by, sponsored by, or in any way officially connected to Instagram, TikTok, WhatsApp or Meta Platforms, Inc. \"Instagram\", \"TikTok\" and \"WhatsApp\" are trademarks of their respective owners. Any \"Instagram-style\" reference describes a visual design approach only." },
      { h: "Demo content", p: "Some stores, products, imagery and reviews shown on the public site may be for demonstration purposes and do not represent real offers." },
      { h: "No professional advice", p: "Content on this site is provided for general information only and does not constitute legal, financial or tax advice. Merchants should seek their own professional advice." },
      { h: "External links", p: "Our sites may link to other CasperBrands products such as TikTokWebstore.com and WAwebstore.com, and to third-party websites. We are not responsible for the content of external sites." },
    ],
  },
  imprint: {
    title: "Contact & Imprint",
    intro: "StoreIG is operated by CasperBrands. Reach us using the details below.",
    sections: [
      { h: "Company", p: "CasperBrands — StoreIG. (Add your registered company name, address and registration / VAT number here.)" },
      { h: "Email", p: "General enquiries: support@storeig.com · Privacy requests: privacy@storeig.com" },
      { h: "Support hours", p: "We aim to respond to all messages within 1–2 business days." },
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const [overrides, setOverrides] = useState({});
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { api.get("/public/legal").then((r) => setOverrides(r.data || {})).catch(() => setOverrides({})); }, []);

  const base = LEGAL_DEFAULTS[slug];
  if (!base) return <Navigate to="/legal/terms" replace />;
  const ov = overrides[slug] || {};
  const page = {
    title: ov.title || base.title,
    intro: ov.intro ?? base.intro,
    sections: ov.sections?.length ? ov.sections : base.sections,
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white" data-testid={`legal-page-${slug}`}>
      <header className="max-w-5xl mx-auto flex items-center justify-between px-6 md:px-10 h-20">
        <Link to="/" style={anton} className="text-2xl tracking-wide" data-testid="legal-home-link">
          STORE<span style={{ color: LIME }}>IG</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors" data-testid="legal-back">
          <ArrowLeft size={16} /> Back to StoreIG
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-[220px_1fr] gap-10">
        <aside className="lg:sticky lg:top-8 h-max">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-white/40 mb-4">Legal</div>
          <ul className="space-y-1.5">
            {LEGAL_LINKS.map((l) => (
              <li key={l.slug}>
                <Link to={`/legal/${l.slug}`} data-testid={`legal-nav-${l.slug}`}
                  className={`block rounded-lg px-3 py-2 text-sm transition-colors ${l.slug === slug ? "bg-white/10 text-white font-semibold" : "text-white/55 hover:text-white hover:bg-white/5"}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </aside>

        <article>
          <h1 style={anton} className="text-5xl md:text-6xl tracking-tight leading-none">{page.title}</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-white/35 mt-4">Last updated · {UPDATED}</p>
          <p className="text-white/70 mt-6 leading-relaxed max-w-2xl">{page.intro}</p>
          <div className="mt-10 space-y-8 max-w-2xl">
            {page.sections.map((s, i) => (
              <section key={i} data-testid={`legal-section-${i}`}>
                <h2 className="text-lg font-bold text-white">{s.h}</h2>
                <p className="text-white/55 mt-2 leading-relaxed text-sm whitespace-pre-line">{s.p}</p>
              </section>
            ))}
          </div>
          <div className="mt-14 pt-8 border-t border-white/10 text-xs text-white/40">
            © {new Date().getFullYear()} StoreIG — a CasperBrands company. Questions? Email support@storeig.com.
          </div>
        </article>
      </main>
    </div>
  );
}
